/**
 * Paragraph Widget (Story 5.24, Task 3)
 *
 * Renders body text with configurable alignment.
 * Supports auto-sizing via useAutoResize hook.
 * Content is plain text only — no HTML rendering (XSS prevention).
 */

import type { WidgetConfig } from "../../../lib/templateUtils";
import { useAutoResize } from "../../../lib/useAutoResize";

interface ParagraphWidgetProps {
  widget: WidgetConfig;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
}

type ParagraphAlign = "left" | "center" | "right" | "justify";

/** Row span at which content becomes scrollable to prevent excessive widget height */
const OVERFLOW_ROW_THRESHOLD = 8;

const ALIGN_CLASSES: Record<ParagraphAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
  justify: "text-justify",
};

export function ParagraphWidget({ widget, onAutoResize }: ParagraphWidgetProps) {
  const config = widget.config as { content?: string; align?: ParagraphAlign };
  const content = config.content ?? "";
  const align = config.align ?? "left";
  const currentRowSpan = widget.position.rowSpan ?? 1;
  const isAutoSized = widget.autoSize !== false;

  const containerRef = useAutoResize({
    currentRowSpan,
    enabled: isAutoSized && !!content && !!onAutoResize,
    onAutoResize: (newRowSpan) => onAutoResize?.(widget.id, newRowSpan),
  });

  if (!content) {
    return (
      <div data-testid="widget-type-paragraph" className="flex h-full flex-col p-4">
        <h3 className="mb-2 truncate text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
          {widget.title}
        </h3>
        <div className="flex flex-1 items-center justify-center">
          <span className="text-[13.5px] italic text-muted-foreground" data-testid="paragraph-empty">
            Add paragraph text...
          </span>
        </div>
      </div>
    );
  }

  const overflowClass = currentRowSpan >= OVERFLOW_ROW_THRESHOLD ? "overflow-y-auto" : "";

  return (
    <div data-testid="widget-type-paragraph" className="flex h-full flex-col p-4">
      <h3 className="mb-2 truncate text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
        {widget.title}
      </h3>
      <div ref={containerRef} className={`min-w-0 flex-1 ${overflowClass}`}>
        <p className={`text-[13.5px] leading-relaxed text-foreground ${ALIGN_CLASSES[align]}`} data-testid="paragraph-content">
          {content}
        </p>
      </div>
    </div>
  );
}
